<template>
  <HeadlessListbox as="div">
    <HeadlessListboxLabel
      v-if="label"
      class="mb-1 block text-left text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </HeadlessListboxLabel>

    <div ref="listboxReferenceRef">
      <HeadlessListboxButton as="template">
        <slot name="button">
          <button
            type="button"
            :class="[
              'flex w-full flex-nowrap items-center justify-between rounded-md border px-3 py-2 text-sm font-semibold focus:outline-none sm:text-sm',
              hasError
                ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
                : 'focus:border-primary focus:ring-primary',
            ]"
          >
            <span class="mr-2 line-clamp-1">
              {{ selectedLabel }}
            </span>
            <IconChevronDown
              class="rounded-full text-background-fg/40"
            />
          </button>
        </slot>
      </HeadlessListboxButton>
    </div>

    <Teleport to="body">
      <div ref="listboxOptionsRef" :style="floatingStyles">
        <Transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <HeadlessListboxOptions
            class="mb-6 max-h-60 w-full overflow-y-auto rounded-lg bg-background shadow"
          >
            <HeadlessListboxOption
              v-for="option in options"
              :key="option.value?.toString()"
            >
              <div
                :class="[
                  modelValue === option.value
                    ? 'bg-background-fg/5'
                    : 'text-background-fg/50 hover:bg-background-fg/1',
                  'flex cursor-pointer items-center justify-start px-3 py-2',
                ]"
                @click="modelValue = option.value"
              >
                <component
                  :is="option.icon"
                  v-if="option.icon"
                  class="mr-2"
                />
                <span
                  class="line-clamp-1 text-left text-sm font-semibold"
                >
                  {{ option.label }}
                </span>
              </div>
            </HeadlessListboxOption>
          </HeadlessListboxOptions>
        </Transition>
      </div>
    </Teleport>
  </HeadlessListbox>
</template>

<script
  setup
  lang="ts"
  generic="
    TValue extends
      | string
      | number
      | null
      | boolean
      | undefined
  "
>
import type { Option } from "@/composables/options/options"
import {
  useFloating,
  offset,
  flip,
  autoUpdate,
  size,
} from "@floating-ui/vue"

const props = defineProps<{
  options: Option<TValue>[]
  label?: string
  hasError?: boolean
  name?: string
}>()

const modelValue = defineModel<TValue>({ required: true })

const selectedLabel = computed(
  () =>
    props.options.find(
      ({ value }) => value === modelValue.value
    )?.label || modelValue.value
)

const listboxOptionsRef = useTemplateRef(
  "listboxOptionsRef"
)
const listboxReferenceRef = useTemplateRef(
  "listboxReferenceRef"
)

const { floatingStyles } = useFloating(
  listboxReferenceRef,
  listboxOptionsRef,

  {
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          })
        },
      }),
    ],
  }
)
</script>
